<template>
  <div :data-testid="dataTestid" class="h-full">
    <TsMedia
      :append-class="
        twMerge(
          'h-full relative bg-natural-soft-white rounded-lg p-3 md:items-center',
          props.appendClass
        )
      "
    >
      <TsMediaStart :appendClass="twMerge(props.startClass)" v-if="!hideImage">
        <slot name="start">
          <NuxtLinkWithLocale
            v-if="store_data.link"
            :to="store_data.link"
            :data-testid="dataTestid"
          >
            <TsImage
              :src="store_data.image ?? '/images/logos/store.svg'"
              :alt="props.store_data.alt"
              width="70"
              height="23"
              :data-testid="props.dataTestid + '-card-branch-info-image'"
              loading="lazy"
            />
          </NuxtLinkWithLocale>
        </slot>
      </TsMediaStart>
      <TsMediaContent
        :append-class="twMerge('max-md:mr-3', props.contentClass)"
      >
        <slot name="content">
          <NuxtLinkWithLocale
            v-if="store_data.link"
            :to="store_data.link"
            :data-testid="dataTestid"
          >
            <TsTypography
              :data-testid="props.dataTestid + '-card-branch-info-title'"
              variant="h4"
              append-class="mb-0 flex justify-between"
            >
              <span class="line-clamp-3 hover:text-primary transition-colors">{{ `Toolstation ${store_data.name}` }} </span>
              <span
                v-if="variant === 'detailed' && showRating"
                class="flex items-center justify-end space-x-1"
              >
                <TsIcon
                  name="gravity-ui:star-fill"
                  class="text-warning-dark"
                  size="20"
                  :data-testid="props.dataTestid + '-card-branch-info-icon'"
                />
                <TsTypography variant="sm" append-class="mb-0"
                  >4.4</TsTypography
                >
              </span>
              <TsIcon
                v-if="!props.withoutIcon"
                size="28"
                class="p-0"
                name="material-symbols-light:chevron-right"
              />
            </TsTypography>
            <TsTypography
              :data-testid="props.dataTestid + '-card-branch-info-title'"
              variant="sm"
            >
              {{ store_data.address_text?.replace(/^.*?,\s*/, "") }}
            </TsTypography>
          </NuxtLinkWithLocale>

          <TsTypography
            v-if="variant === 'detailed'"
            variant="sm"
            append-class="max-md:mt-2 mt-0.5 md:mb-0 flex flex-wrap items-center gap-2"
          >
            <span
              v-if="props.store_data.distance"
              class="flex items-center space-x-1"
            >
              <TsIcon name="weui:location-outlined" size="16" />
              <span
                >{{ convertMilesToKm(props.store_data.distance).toFixed(1) }}
                {{ useTranslation("Kilometers", "km") }}</span
              >
            </span>
            <span
              class="flex items-center cursor-pointer space-x-1 text-info sm:ms-auto"
              @click.stop="emit('showDirection')"
            >
              <TsIcon name="teenyicons:direction-outline" size="14" />
              <span class="underline hover:no-underline">{{
                useTranslation("direction", "Direction")
              }}</span>
            </span>
          </TsTypography>
        </slot>
      </TsMediaContent>
      <TsMediaEnd :append-class="twMerge('text-right', props.endClass)">
        <slot name="end"> </slot>
      </TsMediaEnd>
    </TsMedia>
  </div>
</template>

<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { useTranslation } from "~/composables/useTranslation";
const {convertMilesToKm} = await import('~/utils/branch.utlis')


const TsMediaStart = defineAsyncComponent(
    () => import('~/components/organisms/TsMediaStart.vue')
);

const TsMediaContent = defineAsyncComponent(
    () => import('~/components/organisms/TsMediaContent.vue')
);

const TsMediaEnd = defineAsyncComponent(
    () => import('~/components/organisms/TsMediaEnd.vue')
);

type Variants = "minimal" | "detailed";

type Props = {
  variant?: Variants;
  appendClass?: string;
  dataTestid?: string;
  store_data?: {
    image?: string;
    alt?: string;
    name?: string;
    link?: string;
    address_text?: string;
    opens_at?: string;
    distance?: number;
    rating?: string;
  };
  startClass?: string;
  contentClass?: string;
  endClass?: string;
  withoutIcon?: boolean;
  showRating?: boolean;
  hideImage?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  variant: "detailed",
  store_data: () => ({
    image: "/images/logos/store.svg",
    alt: "store-img",
    name: "Alkmaar",
    address_text: "Industrieweg 2, v3771MD, Barneveld, Nederland",
    icon: "material-symbols:kid-star-sharp",
    link: "#",
    hideImage: false,
  }),
});

const emit = defineEmits<{
  showDirection: any;
}>();
</script>
